import React, { Dispatch, SetStateAction, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { Button } from 'shared/components';
import { SmallLoader } from 'shared/components/loading';
import { GqlImageBanner, ImageBannerType, useGetCollectionsQuery } from 'types/graphql';
import filteredSpecials from 'shared/graphql/special/queries/filtered-specials.gql';
import { useStores } from 'src/hooks/use-stores';

import {
  Container,
  Description,
  SearchContainer,
  SearchInput,
  ItemList,
  ItemRow,
  ItemImage,
  ItemImagePlaceholder,
  ItemInfo,
  ItemTitle,
  ItemMeta,
  ItemStatus,
  EmptyState,
  LoadingState,
  SelectedInfo,
  SelectedLabel,
  ClearButton,
} from './connection-step.styles';
import { ButtonContainer } from '../configuration-step/configuration-step.styles';

type ConnectionStepProps = {
  banner: GqlImageBanner | undefined;
  currentStepIndex: number;
  setStepNumber: (newStep: number) => void;
  setBannerData: (data: Partial<GqlImageBanner>) => void;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
  isReadOnly?: boolean;
};

type SelectableItem = {
  id: string;
  title: string;
  image?: string | null;
  meta?: string;
  isActive?: boolean;
  type: string;
};

export const TEST_ID_CONNECTION_STEP = 'image-banner-connection-step';

/**
 * Validates that an image URL is an absolute URL, not a relative path
 * Returns true for full URLs (http://, https://, or protocol-relative //)
 */
function isAbsoluteUrl(url: string | null | undefined): boolean {
  if (!url) {
    return false;
  }
  return /^(https?:)?\/\//i.test(url);
}

/**
 * Connection step for selecting the entity to connect the image banner to
 * Displays a list of specials, collections, or categories based on the banner type
 */
export function ConnectionStep({
  banner,
  currentStepIndex,
  setStepNumber,
  setBannerData,
  setIsDirty,
  isReadOnly = false,
}: ConnectionStepProps): JSX.Element {
  const { id: dispensaryId } = useParams<{ id: string }>();
  const { apolloClient } = useStores();
  const [searchTerm, setSearchTerm] = useState('');

  const bannerType = banner?.bannerType ?? ImageBannerType.link;
  const selectedId = banner?.connectedId;

  // Fetch specials
  const { data: specialsData, loading: loadingSpecials } = useQuery(filteredSpecials, {
    skip: bannerType !== ImageBannerType.special,
    client: apolloClient,
    variables: {
      specialsFilter: {
        dispensaryId,
        current: true,
      },
      translateSpecialsToV3: true,
    },
  });

  // Fetch collections (non-custom categories)
  const { data: collectionsData, loading: loadingCollections } = useGetCollectionsQuery({
    skip: bannerType !== ImageBannerType.collection,
    client: apolloClient,
    variables: { dispensaryId, customCategory: false },
  });

  // Fetch custom categories
  const { data: customCategoriesData, loading: loadingCustomCategories } = useGetCollectionsQuery({
    skip: bannerType !== ImageBannerType.category,
    client: apolloClient,
    variables: { dispensaryId, customCategory: true },
  });

  const isLoading = loadingSpecials || loadingCollections || loadingCustomCategories;

  /**
   * Get the list of selectable items based on the banner type
   */
  const items = useMemo((): SelectableItem[] => {
    switch (bannerType) {
      case ImageBannerType.special: {
        const specials = specialsData?.filteredSpecials?.specials ?? [];
        return specials.map((special: any) => ({
          id: special._id,
          title: special.name || special.menuDisplayName || 'Unnamed Special',
          image: special.menuDisplayImage,
          meta: special.specialType === 'bogo' ? 'Offer' : 'Sale',
          isActive: special.active,
          type: 'Special',
        }));
      }
      case ImageBannerType.collection: {
        const collections = collectionsData?.getCollections ?? [];
        return collections.map((collection) => ({
          id: collection.id,
          title: collection.title || 'Unnamed Collection',
          image: collection.image,
          meta: `${collection.productCount || 0} products`,
          isActive: collection.enabled !== false,
          type: 'Collection',
        }));
      }
      case ImageBannerType.category: {
        const categories = customCategoriesData?.getCollections ?? [];
        return categories.map((category) => ({
          id: category.id,
          title: category.title || 'Unnamed Category',
          image: category.image,
          meta: `${category.productCount || 0} products`,
          isActive: category.enabled !== false,
          type: 'Collection', // Custom category is collection on backend so we use the same type
        }));
      }
      default:
        return [];
    }
  }, [bannerType, specialsData, collectionsData, customCategoriesData]);

  /**
   * Filter items by search term
   */
  const filteredItems = useMemo(() => {
    if (!searchTerm) {
      return items;
    }
    const lowerSearchTerm = searchTerm.toLowerCase();
    return items.filter((item) => item.title.toLowerCase().includes(lowerSearchTerm));
  }, [items, searchTerm]);

  /**
   * Get the currently selected item
   */
  const selectedItem = useMemo(() => {
    if (!selectedId) {
      return null;
    }
    return items.find((item) => item.id === selectedId) ?? null;
  }, [items, selectedId]);

  /**
   * Handle item selection
   * Auto-populates banner images and alt text from the connected entity
   */
  function handleSelectItem(item: SelectableItem): void {
    if (isReadOnly) {
      return;
    }

    // Build update data with connection info
    const updateData: Partial<GqlImageBanner> = {
      ...banner,
      connectedId: item.id,
      connectedType: item.type,
    };

    // Auto-populate alt text from connected entity name
    const isChangingConnection = banner?.connectedId !== item.id;
    if (!banner?.alt || isChangingConnection) {
      updateData.alt = item.title;
    }

    // Auto-populate images from connected entity if available
    // Only use images that are absolute URLs, not relative paths
    if (item.image && isAbsoluteUrl(item.image)) {
      // Set desktop image if no existing image or if changing connection
      if (!banner?.image || isChangingConnection) {
        updateData.image = item.image;
      }
      // Set mobile image if no existing image or if changing connection
      if (!banner?.mobileImage || isChangingConnection) {
        updateData.mobileImage = item.image;
      }
    }

    setBannerData(updateData);
    setIsDirty(true);
  }

  /**
   * Clear the selection
   */
  function handleClearSelection(): void {
    if (isReadOnly) {
      return;
    }

    setBannerData({
      ...banner,
      connectedId: undefined,
      connectedType: undefined,
    });
    setIsDirty(true);
  }

  /**
   * Handle continue button click
   */
  function handleNext(): void {
    setStepNumber(currentStepIndex + 2);
  }

  /**
   * Get the description text based on banner type
   */
  function getDescription(): string {
    switch (bannerType) {
      case ImageBannerType.special:
        return 'Select a special or discount to connect to this image banner. When clicked, customers will be directed to the special details page.';
      case ImageBannerType.collection:
        return 'Select a product collection to connect to this image banner. When clicked, customers will be directed to the collection page.';
      case ImageBannerType.category:
        return 'Select a custom category to connect to this image banner. When clicked, customers will be directed to the category page.';
      default:
        return '';
    }
  }

  /**
   * Get the placeholder text for the search input
   */
  function getSearchPlaceholder(): string {
    switch (bannerType) {
      case ImageBannerType.special:
        return 'Search specials...';
      case ImageBannerType.collection:
        return 'Search collections...';
      case ImageBannerType.category:
        return 'Search categories...';
      default:
        return 'Search...';
    }
  }

  // Don't render for link type
  if (bannerType === ImageBannerType.link) {
    return (
      <Container data-testid={TEST_ID_CONNECTION_STEP}>
        <Description>No connection needed for link banners. Click Next to continue.</Description>
        <ButtonContainer>
          <Button onClick={handleNext}>Next</Button>
        </ButtonContainer>
      </Container>
    );
  }

  return (
    <Container data-testid={TEST_ID_CONNECTION_STEP}>
      <Description>{getDescription()}</Description>

      {selectedItem && (
        <SelectedInfo>
          <SelectedLabel>
            Selected: <strong>{selectedItem.title}</strong>
          </SelectedLabel>
          {!isReadOnly && <ClearButton onClick={handleClearSelection}>Clear</ClearButton>}
        </SelectedInfo>
      )}

      <SearchContainer>
        <SearchInput
          type='text'
          placeholder={getSearchPlaceholder()}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={isReadOnly}
        />
      </SearchContainer>

      <ItemList>
        {isLoading && (
          <LoadingState>
            <SmallLoader />
          </LoadingState>
        )}
        {!isLoading && filteredItems.length === 0 && (
          <EmptyState>{searchTerm ? 'No items match your search.' : 'No items available.'}</EmptyState>
        )}
        {!isLoading &&
          filteredItems.length > 0 &&
          filteredItems.map((item) => (
            <ItemRow key={item.id} $selected={item.id === selectedId} onClick={() => handleSelectItem(item)}>
              {item.image ? (
                <ItemImage src={item.image} alt={item.title} />
              ) : (
                <ItemImagePlaceholder>No img</ItemImagePlaceholder>
              )}
              <ItemInfo>
                <ItemTitle>{item.title}</ItemTitle>
                <ItemMeta>
                  {item.meta}
                  <ItemStatus $active={item.isActive}>{item.isActive ? 'Active' : 'Inactive'}</ItemStatus>
                </ItemMeta>
              </ItemInfo>
            </ItemRow>
          ))}
      </ItemList>

      <ButtonContainer>
        <Button onClick={handleNext} disabled={!selectedId}>
          Next
        </Button>
      </ButtonContainer>
    </Container>
  );
}
